import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineArrowLeft,
} from "react-icons/hi2";
import {
  authService,
  capaService,
  rcaService,
  masterDataService,
  userService,
  breakdownLogService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import PageLoading from "../components/common/PageLoading";
import Submitsbo from "./SafetyBehaviorObservation/schedule/Submitsbo";
import SharedModal from "../components/common/SharedModal";
import "./CAPAEntry.css";
import { HiOutlineShieldCheck } from "react-icons/hi2";

// Synchronous cache loading function
const getCachedCAPAEntryData = () => {
  try {
    const cachedRcaRecords = sessionStorage.getItem("rca_records_data");
    const cachedBreakdownLogs = sessionStorage.getItem("breakdown_logs_data");
    const cachedCapaStatuses = sessionStorage.getItem("capa_statuses_data");
    const cachedUsers = sessionStorage.getItem("users_data");

    return {
      rcaRecords: cachedRcaRecords ? JSON.parse(cachedRcaRecords) : [],
      breakdownLogs: cachedBreakdownLogs ? JSON.parse(cachedBreakdownLogs) : [],
      statuses: cachedCapaStatuses ? JSON.parse(cachedCapaStatuses) : [],
      users: cachedUsers ? JSON.parse(cachedUsers) : [],
      hasAll: !!(
        cachedRcaRecords &&
        cachedBreakdownLogs &&
        cachedCapaStatuses &&
        cachedUsers
      ),
    };
  } catch (e) {
    console.error("Error parsing cached CAPA Entry data:", e);
    return {
      rcaRecords: [],
      breakdownLogs: [],
      statuses: [],
      users: [],
      hasAll: false,
    };
  }
};

const CAPAEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const rcaIdFromQuery = searchParams.get("rcaId");
  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();
  const { hasPermission } = usePermissions();
  const isEditMode = !!id;

  // Permission checks for CAPA
  const canCreate = hasPermission("CAPA.Create");
  const canEdit = hasPermission("CAPA.Edit");

  // Permission checks for CAPA Actions
  const canViewAction = hasPermission("CAPAAction.View");
  const canCreateAction = hasPermission("CAPAAction.Create");
  const canEditAction = hasPermission("CAPAAction.Edit");
  const canDeleteAction = hasPermission("CAPAAction.Delete");

  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedCAPAEntryData();
  // For edit mode, always show loading initially (need to load CAPA data)
  // For create mode, only show loading if no cached data
  const [loading, setLoading] = useState(
    isEditMode ? true : !cachedInitialData.hasAll,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [rcaRecords, setRcaRecords] = useState(cachedInitialData.rcaRecords);
  const [breakdownLogs, setBreakdownLogs] = useState(
    cachedInitialData.breakdownLogs,
  );
  const [statuses, setStatuses] = useState(cachedInitialData.statuses);
  const [users, setUsers] = useState(cachedInitialData.users);
  const [tasks, setTasks] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [isSboModalOpen, setIsSboModalOpen] = useState(false);
  const [selectedSboRoundId, setSelectedSboRoundId] = useState(null);
  const [sboNumber, setSboNumber] = useState("");

  const [formData, setFormData] = useState({
    rcaId: "",
    breakdownLogId: "",
    title: "",
    description: "",
    capaType: "Corrective",
    assignedTo: "",
    targetDate: "",
    statusId: "",
    effectivenessReview: "",
  });

  const [taskFormData, setTaskFormData] = useState({
    taskDescription: "",
    assignedTo: "",
    dueDate: "",
    statusId: "",
    notes: "",
    priority: 3,
  });

  const [formErrors, setFormErrors] = useState({});
  const [taskFormErrors, setTaskFormErrors] = useState({});

  useEffect(() => {
    if (tenantId) {
      // Check permissions for loading data
      if (isEditMode && !canEdit) {
        setError("You do not have permission to edit CAPA records");
        setLoading(false);
        return;
      }
      if (!isEditMode && !canCreate) {
        setError("You do not have permission to create CAPA records");
        setLoading(false);
        return;
      }
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadInitialData(!hasCachedData);
    } else {
      setError("Please login to work with CAPA records");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, rcaIdFromQuery, isEditMode, canCreate, canEdit]);

  const loadInitialData = async (showLoading = true) => {
    try {
      if (showLoading && !cachedInitialData.hasAll) {
        setLoading(true);
      }
      setError("");

      const [rcaData, breakdownData, statusesData, usersData] =
        await Promise.all([
          rcaService.getRcaRecords(),
          breakdownLogService.getBreakdownLogs().catch(() => []),
          masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
          userService.getAllUsers?.() || Promise.resolve([]),
        ]);

      setRcaRecords(rcaData || []);
      setBreakdownLogs(breakdownData || []);
      setUsers(usersData || []);

      // Filter statuses for CAPA
      const allStatuses = Array.isArray(statusesData) ? statusesData : [];
      let capaStatuses = allStatuses.filter((s) => s.statusType === "CAPA");

      // Check for required default statuses and create them if missing (Auto-Seeding)
      const requiredStatuses = [
        "Pending",
        "In Process",
        "Completed",
        "Cancelled",
      ];
      const missingStatuses = requiredStatuses.filter(
        (reqStatus) =>
          !capaStatuses.some(
            (s) =>
              s.statusName?.toLowerCase().replace(/\s/g, "") ===
              reqStatus.toLowerCase().replace(/\s/g, ""),
          ),
      );

      if (missingStatuses.length > 0 && tenantId) {
        try {
          console.log("Seeding missing default statuses:", missingStatuses);
          // Create statuses sequentially to avoid race conditions
          for (const statusName of missingStatuses) {
            await masterDataService.createStatus({
              statusName: statusName,
              statusType: "CAPA",
              tenantId: parseInt(tenantId, 10),
              isActive: true,
              description: `Default CAPA status: ${statusName}`,
            });
          }

          // Re-fetch statuses after creation to get real IDs
          const updatedStatusesData =
            await masterDataService.getStatuses(tenantId);
          const updatedAllStatuses = Array.isArray(updatedStatusesData)
            ? updatedStatusesData
            : [];
          capaStatuses = updatedAllStatuses.filter(
            (s) => s.statusType === "CAPA",
          );
        } catch (err) {
          console.error("Failed to seed default statuses:", err);
          // If seeding fails, we have no choice but to fallback or show error.
          // We will fallback to "Fake" IDs but this will likely fail on save if FK exists.
        }
      }

      // Sort statuses to match the required order, ensuring uniqueness by name
      const orderedStatuses = [];
      const addedNames = new Set();

      const normalizeName = (name) =>
        name ? name.toLowerCase().replace(/\s/g, "") : "";

      requiredStatuses.forEach((reqStatus) => {
        // Find ALL matching statuses in DB (could be duplicates)
        const matchingStatuses = capaStatuses.filter(
          (s) => normalizeName(s.statusName) === normalizeName(reqStatus),
        );

        // Take the first one (usually the oldest or valid one) and ignore others
        if (matchingStatuses.length > 0) {
          const primary = matchingStatuses[0];
          const key = normalizeName(primary.statusName);

          if (!addedNames.has(key)) {
            orderedStatuses.push(primary);
            addedNames.add(key);
          }
        }
      });

      // Add any remaining valid statuses that weren't in the required list
      capaStatuses.forEach((s) => {
        const key = normalizeName(s.statusName);

        // Uniqueness check by ID and Name
        if (
          !orderedStatuses.some((os) => os.statusId === s.statusId) &&
          !addedNames.has(key)
        ) {
          orderedStatuses.push(s);
          addedNames.add(key);
        }
      });

      setStatuses(orderedStatuses);

      // Cache the data
      sessionStorage.setItem("rca_records_data", JSON.stringify(rcaData || []));
      sessionStorage.setItem(
        "breakdown_logs_data",
        JSON.stringify(breakdownData || []),
      );
      sessionStorage.setItem(
        "capa_statuses_data",
        JSON.stringify(orderedStatuses),
      );
      sessionStorage.setItem("users_data", JSON.stringify(usersData || []));

      if (isEditMode) {
        await loadCapaData();
      } else if (rcaIdFromQuery || location.state?.rcaId) {
        // Pre-fill RCA details if coming from RCA/5 Whys page
        const rcaSourceId = rcaIdFromQuery || location.state?.rcaId;
        const problemDesc = location.state?.problemDescription || "";
        const rootCause = location.state?.rootCause || "";

        // If root cause exists, use it as description. Otherwise fallback to problem description.
        const description = rootCause || problemDesc;

        setFormData((prev) => ({
          ...prev,
          rcaId: rcaSourceId,
          description: description,
          title: problemDesc || prev.title,
        }));
      }

      // Handle SBO observation link data from navigation state
      if (location.state?.observationRoundId) {
        setSelectedSboRoundId(location.state.observationRoundId);
      }
      if (location.state?.observationNumber) {
        setSboNumber(location.state.observationNumber);
      }
    } catch (err) {
      console.error("Error loading CAPA initial data:", err);
      setError("Failed to load data. Please try again.");
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setRcaRecords([]);
        setBreakdownLogs([]);
        setStatuses([]);
        setUsers([]);
      }
    } finally {
      // Always set loading to false after data load completes, especially in edit mode
      if (showLoading || isEditMode) {
        setLoading(false);
      }
    }
  };

  const loadCapaData = async () => {
    try {
      const capa = await capaService.getCapaRecordById(id);
      setFormData({
        rcaId: capa.rcaId?.toString() || "",
        breakdownLogId: capa.breakdownLogId?.toString() || "",
        title: capa.title || "",
        description: capa.description || "",
        capaType: capa.capaType || "Corrective",
        assignedTo: capa.assignedTo?.toString() || "",
        targetDate: capa.targetDate
          ? new Date(capa.targetDate).toISOString().split("T")[0]
          : "",
        statusId: capa.statusId?.toString() || "",
        effectivenessReview: capa.effectivenessReview || "",
      });

      if (capa.observationRoundId || capa.roundId) {
        setSelectedSboRoundId(capa.observationRoundId || capa.roundId);
      }
      if (capa.observationNumber) {
        setSboNumber(capa.observationNumber);
      } else if (capa.description && capa.description.match(/SBO-\d{4}-\d+/)) {
        setSboNumber(capa.description.match(/SBO-\d{4}-\d+/)[0]);
      }

      // Load tasks
      if (capa.actions && capa.actions.length > 0) {
        setTasks(capa.actions);
      } else {
        // Fetch tasks separately if not included
        const tasksData = await capaService.getCapaActionsByCapa(id);
        setTasks(tasksData || []);
      }
    } catch (err) {
      console.error("Error loading CAPA record:", err);
      setError("Failed to load CAPA record. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setTaskFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};

    // RCA is now optional - removed validation
    if (!formData.title?.trim()) {
      errors.title = "Title is required";
    }
    if (
      !formData.statusId ||
      formData.statusId === "" ||
      isNaN(parseInt(formData.statusId, 10))
    ) {
      errors.statusId = "Status is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTaskForm = () => {
    const errors = {};

    if (!taskFormData.taskDescription?.trim()) {
      errors.taskDescription = "Task description is required";
    }
    if (!taskFormData.statusId || taskFormData.statusId === "") {
      errors.statusId = "Status is required";
    }

    setTaskFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (isEditMode && !canEdit) {
      setError("You do not have permission to edit CAPA records");
      return;
    }
    if (!isEditMode && !canCreate) {
      setError("You do not have permission to create CAPA records");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const statusIdNum = parseInt(formData.statusId, 10);
      if (!statusIdNum || statusIdNum <= 0) {
        setFormErrors({ statusId: "Please select a valid status" });
        setSubmitting(false);
        return;
      }

      // Payload must match CapaRecordRequest DTO
      const payload = {
        rcaId:
          formData.rcaId && formData.rcaId !== ""
            ? parseInt(formData.rcaId, 10)
            : null,
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        capaType: formData.capaType || "Corrective",
        assignedTo: formData.assignedTo
          ? parseInt(formData.assignedTo, 10)
          : null,
        targetDate: formData.targetDate
          ? new Date(formData.targetDate).toISOString()
          : null,
        statusId: statusIdNum,
        effectivenessReview: formData.effectivenessReview || "",
        // Include SBO observation link fields
        observationRoundId: selectedSboRoundId ? parseInt(selectedSboRoundId, 10) : (location.state?.observationRoundId ? parseInt(location.state.observationRoundId, 10) : null),
        observationItemId: location.state?.observationItemId ? parseInt(location.state.observationItemId, 10) : null,
        observationNumber: sboNumber || location.state?.observationNumber || null,
      };

      console.log("Submitting CAPA record with payload:", payload);

      if (isEditMode) {
        payload.capaId = parseInt(id, 10);
        await capaService.updateCapaRecord(id, payload);
        setSuccess("CAPA record updated successfully!");
      } else {
        await capaService.createCapaRecord(payload);
        setSuccess("CAPA record created successfully!");
      }

      setTimeout(() => {
        navigate("/home/capa");
      }, 1200);
    } catch (err) {
      console.error("Error saving CAPA record:", err);

      // DEBUG: Capture full error details to show user/dev
      let errorMessage = "An error occurred.";

      if (err.response) {
        console.log("Server Response Data:", err.response.data);
        if (err.response.data.errors) {
          // Standard Validation Errors
          const validationErrors = Object.values(err.response.data.errors)
            .flat()
            .join(", ");
          errorMessage = `Validation Failed: ${validationErrors}`;

          // Map to field errors
          const newFormErrors = {};
          for (const key in err.response.data.errors) {
            const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
            newFormErrors[fieldName] = err.response.data.errors[key][0];
          }
          setFormErrors(newFormErrors);
        } else if (err.response.data.error) {
          errorMessage = `Server Error: ${err.response.data.error}`;
        } else if (err.response.data.message) {
          errorMessage = `Server Message: ${err.response.data.message}`;
        } else {
          const rawData =
            typeof err.response.data === "object"
              ? JSON.stringify(err.response.data)
              : err.response.data;
          errorMessage = `Error ${err.response.status}: ${rawData}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTask = () => {
    if (!canCreateAction) {
      setError("You do not have permission to create CAPA actions");
      return;
    }
    setEditingTask(null);
    setTaskFormData({
      taskDescription: "",
      assignedTo: "",
      dueDate: "",
      statusId: "",
      notes: "",
      priority: 3,
    });
    setTaskFormErrors({});
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    if (!canEditAction) {
      setError("You do not have permission to edit CAPA actions");
      return;
    }
    setEditingTask(task);
    setTaskFormData({
      taskDescription: task.taskDescription || "",
      assignedTo: task.assignedTo?.toString() || "",
      dueDate: task.dueDate
        ? new Date(task.dueDate).toISOString().split("T")[0]
        : "",
      statusId: task.statusId?.toString() || "",
      notes: task.notes || "",
      priority: task.priority || 3,
    });
    setTaskFormErrors({});
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    // Check permissions before submitting
    if (editingTask && !canEditAction) {
      setError("You do not have permission to edit CAPA actions");
      return;
    }
    if (!editingTask && !canCreateAction) {
      setError("You do not have permission to create CAPA actions");
      return;
    }

    if (!validateTaskForm()) {
      return;
    }

    if (!isEditMode || !id) {
      setError("Please save the CAPA record first before adding tasks");
      setShowTaskModal(false);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const taskStatusIdNum = parseInt(taskFormData.statusId, 10);
      if (isNaN(taskStatusIdNum) || taskStatusIdNum <= 0) {
        setTaskFormErrors({ statusId: "Please select a valid status" });
        setSubmitting(false);
        return;
      }

      const taskPayload = {
        capaId: parseInt(id, 10),
        taskDescription: taskFormData.taskDescription.trim(),
        assignedTo: taskFormData.assignedTo
          ? parseInt(taskFormData.assignedTo, 10)
          : null,
        dueDate: taskFormData.dueDate
          ? new Date(taskFormData.dueDate).toISOString()
          : null,
        statusId: taskStatusIdNum,
        notes: taskFormData.notes?.trim() || null,
        priority: parseInt(taskFormData.priority, 10),
      };

      if (editingTask) {
        taskPayload.actionId = editingTask.actionId;
        await capaService.updateCapaAction(editingTask.actionId, taskPayload);
        setSuccess("Task updated successfully!");
      } else {
        await capaService.createCapaAction(taskPayload);
        setSuccess("Task added successfully!");
      }

      // Reload tasks
      const updatedTasks = await capaService.getCapaActionsByCapa(id);
      setTasks(updatedTasks || []);

      setShowTaskModal(false);
      setEditingTask(null);
      setTaskFormData({
        taskDescription: "",
        assignedTo: "",
        dueDate: "",
        statusId: "",
        notes: "",
        priority: 3,
      });
    } catch (err) {
      console.error("Error saving task:", err);
      setError(err.response?.data?.message || "Failed to save task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canDeleteAction) {
      setError("You do not have permission to delete CAPA actions");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      await capaService.deleteCapaAction(taskId);
      setSuccess("Task deleted successfully!");
      const updatedTasks = await capaService.getCapaActionsByCapa(id);
      setTasks(updatedTasks || []);
    } catch (err) {
      console.error("Error deleting task:", err);
      setError("Failed to delete task");
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatusId) => {
    try {
      await capaService.updateCapaActionStatus(taskId, newStatusId);
      setSuccess("Task status updated successfully!");
      const updatedTasks = await capaService.getCapaActionsByCapa(id);
      setTasks(updatedTasks || []);
    } catch (err) {
      console.error("Error updating task status:", err);
      setError("Failed to update task status");
    }
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1:
        return "High";
      case 2:
        return "Medium";
      case 3:
        return "Low";
      default:
        return "Low";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1:
        return "priority-high";
      case 2:
        return "priority-medium";
      case 3:
        return "priority-low";
      default:
        return "priority-low";
    }
  };

  if (loading) {
    return (
      <div className="capa-entry-page pagecontainer">
        <PageLoading message="Loading CAPA…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer capa-entry-page">
      <div className="page-header">
        <div className="header-content">
          <div className="d-flex">
            <button className="oee-report-back-btn mr-2" onClick={() => navigate("/home/capa")}>
              <HiOutlineArrowLeft />

            </button>

            <h1>{isEditMode ? "Edit CAPA" : "Create CAPA"}</h1>
          </div>
          <p>
            {isEditMode
              ? "Update corrective or preventive action details"
              : "Create a new corrective or preventive action from an RCA"}
          </p>
          {isEditMode && sboNumber && (
            <div
              className="sbo-link-badge"
              style={{ marginTop: "10px" }}
              onClick={() => {
                if (selectedSboRoundId) {
                  setIsSboModalOpen(true);
                }
              }}
            >
              <HiOutlineShieldCheck size={14} />
              <span>Linked SBO: {sboNumber}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="capa-entry-container">
        <form onSubmit={handleSubmit} className="capa-entry-form">
          <div className="form-section">
            <h1 className="page-titlenew">CAPA Information</h1>
            <div className="form-row">
              <div className="form-group span-2">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={formErrors.title ? "error" : ""}
                  placeholder="Enter CAPA title"
                />
                {formErrors.title && (
                  <span className="field-error">{formErrors.title}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="capaType">CAPA Type</label>
                <select
                  id="capaType"
                  name="capaType"
                  value={formData.capaType}
                  onChange={handleInputChange}
                >
                  <option value="Corrective">Corrective</option>
                  <option value="Preventive">Preventive</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Enter CAPA description"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="assignedTo">Assigned To</label>
                <select
                  id="assignedTo"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                >
                  <option value="">Select User (Optional)</option>
                  {users.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="targetDate">Target Date</label>
                <input
                  type="date"
                  id="targetDate"
                  name="targetDate"
                  value={formData.targetDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="statusId">
                  Status <span className="required">*</span>
                </label>
                <select
                  id="statusId"
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleInputChange}
                  className={formErrors.statusId ? "error" : ""}
                  disabled={statuses.length === 0}
                >
                  <option value="">
                    {statuses.length === 0
                      ? "No statuses available"
                      : "Select Status"}
                  </option>
                  {statuses.map((status) => (
                    <option key={status.statusId} value={status.statusId}>
                      {status.statusName || `Status ${status.statusId}`}
                    </option>
                  ))}
                </select>
                {formErrors.statusId && (
                  <span className="field-error">{formErrors.statusId}</span>
                )}
              </div>
            </div>
          </div>

          {/* Task Management Section - Only show in edit mode */}
          {isEditMode && (
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tasks</h3>
                {canCreateAction && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleAddTask}
                  >
                    <HiOutlinePlus />
                    Add Task
                  </button>
                )}
              </div>

              {tasks.length === 0 ? (
                <div className="empty-tasks">
                  <p>No tasks added yet. Click "Add Task" to create one.</p>
                </div>
              ) : (
                <div className="tasks-list">
                  {tasks.map((task) => (
                    <div key={task.actionId} className="task-item">
                      <div className="task-header">
                        <div className="task-info">
                          <h4>{task.taskDescription}</h4>
                          <div className="task-badges">
                            <span
                              className={`priority-badge ${getPriorityColor(
                                task.priority,
                              )}`}
                            >
                              {getPriorityLabel(task.priority)}
                            </span>
                            <span className="status-pill">
                              {getStatusName(task.statusId)}
                            </span>
                          </div>
                        </div>
                        <div className="task-actions">
                          <select
                            className="status-select"
                            value={task.statusId || ""}
                            onChange={(e) =>
                              handleUpdateTaskStatus(
                                task.actionId,
                                parseInt(e.target.value, 10),
                              )
                            }
                          >
                            {statuses.map((status) => (
                              <option
                                key={status.statusId}
                                value={status.statusId}
                              >
                                {status.statusName}
                              </option>
                            ))}
                          </select>
                          {canEditAction && (
                            <button
                              type="button"
                              className="btn-icon-edit"
                              onClick={() => handleEditTask(task)}
                              title="Edit Task"
                            >
                              <HiOutlinePencil />
                            </button>
                          )}
                          {canDeleteAction && (
                            <button
                              type="button"
                              className="btn-icon-delete"
                              onClick={() => handleDeleteTask(task.actionId)}
                              title="Delete Task"
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="task-details">
                        {task.assignedTo && (
                          <span>
                            <strong>Assigned:</strong> User {task.assignedTo}
                          </span>
                        )}
                        {task.dueDate && (
                          <span>
                            <strong>Due:</strong>{" "}
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        {task.notes && (
                          <div className="task-notes">
                            <strong>Notes:</strong> {task.notes}
                          </div>
                        )}
                        {task.completedAt && (
                          <span className="completed-date">
                            <strong>Completed:</strong>{" "}
                            {new Date(task.completedAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions" style={{ position: "sticky", bottom: 0, paddingBottom: "15px" }}>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => navigate("/home/capa")}
            >
              <HiOutlineXMark />
              Cancel
            </button>
            <button type="submit" className="btn-primary btn" disabled={submitting}>
              {submitting ? (
                "Saving..."
              ) : (
                <>
                  <HiOutlineCheckCircle />
                  {isEditMode ? "Update CAPA" : "Create CAPA"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Task Modal */}
      <SharedModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title={editingTask ? "Edit Task" : "Add Task"}
        size="lg"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setShowTaskModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn"
              onClick={handleSaveTask}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Task"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group">
            <label htmlFor="taskDescription">
              Task Description <span className="required">*</span>
            </label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={taskFormData.taskDescription}
              onChange={handleTaskInputChange}
              className={taskFormErrors.taskDescription ? "error" : ""}
              rows={3}
              placeholder="Enter task description"
            />
            {taskFormErrors.taskDescription && (
              <span className="field-error">
                {taskFormErrors.taskDescription}
              </span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskAssignedTo">Assigned To</label>
              <select
                id="taskAssignedTo"
                name="assignedTo"
                value={taskFormData.assignedTo}
                onChange={handleTaskInputChange}
              >
                <option value="">Select User (Optional)</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taskDueDate">Due Date</label>
              <input
                type="date"
                id="taskDueDate"
                name="dueDate"
                value={taskFormData.dueDate}
                onChange={handleTaskInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskStatusId">
                Status <span className="required">*</span>
              </label>
              <select
                id="taskStatusId"
                name="statusId"
                value={taskFormData.statusId}
                onChange={handleTaskInputChange}
                className={taskFormErrors.statusId ? "error" : ""}
                disabled={statuses.length === 0}
              >
                <option value="">
                  {statuses.length === 0
                    ? "No statuses available"
                    : "Select Status"}
                </option>
                {statuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName || `Status ${status.statusId}`}
                  </option>
                ))}
              </select>
              {taskFormErrors.statusId && (
                <span className="field-error">
                  {taskFormErrors.statusId}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="taskPriority">Priority</label>
              <select
                id="taskPriority"
                name="priority"
                value={taskFormData.priority}
                onChange={handleTaskInputChange}
              >
                <option value="1">High</option>
                <option value="2">Medium</option>
                <option value="3">Low</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="taskNotes">Notes</label>
            <textarea
              id="taskNotes"
              name="notes"
              value={taskFormData.notes}
              onChange={handleTaskInputChange}
              rows={3}
              placeholder="Enter task notes"
            />
          </div>
        </div>
      </SharedModal>

      {/* SBO Review side drawer */}
      <Submitsbo
        isOpen={isSboModalOpen}
        onClose={() => setIsSboModalOpen(false)}
        roundId={selectedSboRoundId}
        capaId={id}
      />
    </div>
  );
};

export default CAPAEntry;
